'use client'

/**
 * Purpose: Labelled single-choice group used by the survey pages.
 * Inputs: `name` (scopes ids and groups the radios), `label`, `choices`, controlled `value`,
 * `onChange`, optional `beforeLegend` node rendered above the label.
 * Outputs: A `role="radiogroup"` wrapping native radio inputs — the shared `name` gives arrow-key
 * navigation and Space selection natively; the group is named via `aria-labelledby`.
 */

export function RadioGroup({
  name,
  label,
  choices,
  value,
  onChange,
  beforeLegend,
}: {
  name: string
  label: string
  choices: { key: string; label: string }[]
  value: string
  onChange: (val: string) => void
  beforeLegend?: React.ReactNode
}) {
  return (
    <div id={`radio-group-${name}`} role="radiogroup" aria-labelledby={`label-${name}`} className="mb-6">
      {beforeLegend}
      <p id={`label-${name}`} className="question-bold text-black mb-2 leading-snug">{label}</p>
      <div id={`radio-group-${name}-choices`} className="flex flex-col gap-2">
        {/* Native radios share `name`, so the browser handles arrow keys and roving focus. */}
        {choices.map((choice) => (
          <label key={choice.key} id={`radio-group-${name}-choice-${choice.key}`} className="flex items-start gap-3 cursor-pointer">
            <input
              id={`radio-group-${name}-input-${choice.key}`}
              type="radio"
              name={name}
              value={choice.key}
              checked={value === choice.key}
              onChange={() => onChange(choice.key)}
              className="mt-1 accent-orange"
            />
            <span id={`radio-group-${name}-label-${choice.key}`} className="text-black text-sm">{choice.label}</span>
          </label>
        ))}
      </div>
    </div>
  )
}
